/**
 * DataTableProvider Component
 * 
 * Provides TanStack Table context to child components.
 * Supports both controlled (server-side) and uncontrolled (client-side) modes.
 */

import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type RowSelectionState,
  type Table,
  type PaginationState,
  type Updater,
} from '@tanstack/react-table';
import { DEFAULT_PAGE_SIZE } from './constants';

/** Context value shared with child components */
export interface DataTableContextValue<TData> {
  readonly table: Table<TData>;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
}

const DataTableContext = createContext<DataTableContextValue<unknown> | null>(null);

/**
 * Hook to access the DataTable context.
 * Must be used within a DataTableProvider.
 */
export function useDataTable<TData>(): DataTableContextValue<TData> {
  const context = useContext(DataTableContext);
  if (!context) {
    throw new Error('useDataTable must be used within a DataTableProvider');
  }
  return context as DataTableContextValue<TData>;
}

export interface DataTableProviderProps<TData, TValue> {
  readonly columns: ColumnDef<TData, TValue>[];
  readonly data: readonly TData[];
  readonly children: ReactNode;
  
  // Server-side pagination support
  readonly pageCount?: number;
  readonly manualPagination?: boolean;
  readonly manualSorting?: boolean;
  readonly manualFiltering?: boolean;
  
  // Controlled state for server-side operations
  readonly sorting?: SortingState;
  readonly onSortingChange?: (sorting: SortingState) => void;
  
  readonly pagination?: PaginationState;
  readonly onPaginationChange?: (pagination: PaginationState) => void;

  readonly rowSelection?: RowSelectionState;
  readonly onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  readonly enableRowSelection?: boolean;

  // Stable row identity. Defaults to the row index; pass a domain id so a
  // controlled selection survives pagination and refetches.
  readonly getRowId?: (originalRow: TData, index: number) => string;

  // Loading states
  readonly isLoading?: boolean;
  readonly isFetching?: boolean;
}

/** Helper to handle TanStack Table updater pattern */
function resolveUpdater<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' 
    ? (updater as (old: T) => T)(current) 
    : updater;
}

export function DataTableProvider<TData, TValue>({
  columns,
  data,
  children,
  pageCount,
  manualPagination = true,
  manualSorting = false,
  manualFiltering = false,
  sorting: controlledSorting,
  onSortingChange,
  pagination: controlledPagination,
  onPaginationChange,
  rowSelection: controlledSelection,
  onRowSelectionChange,
  enableRowSelection = true,
  getRowId,
  isLoading = false,
  isFetching = false,
}: DataTableProviderProps<TData, TValue>) {
  // Internal state for uncontrolled usage
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalFilters, setInternalFilters] = useState<ColumnFiltersState>([]);
  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>({});
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>({});
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Use controlled state if provided, otherwise use internal
  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;
  const rowSelection = controlledSelection ?? internalSelection;

  const table = useReactTable({
    data: data as TData[],
    columns,
    pageCount,
    getRowId,
    state: {
      sorting,
      columnFilters: internalFilters,
      columnVisibility: internalVisibility,
      rowSelection,
      pagination,
    },
    onSortingChange: (updater) => {
      const newSorting = resolveUpdater(updater, sorting);
      onSortingChange?.(newSorting);
      if (!controlledSorting) setInternalSorting(newSorting);
    },
    onColumnFiltersChange: setInternalFilters,
    onColumnVisibilityChange: setInternalVisibility,
    onRowSelectionChange: (updater) => {
      const newSelection = resolveUpdater(updater, rowSelection);
      onRowSelectionChange?.(newSelection);
      if (!controlledSelection) setInternalSelection(newSelection);
    },
    onPaginationChange: (updater) => {
      const newPagination = resolveUpdater(updater, pagination);
      onPaginationChange?.(newPagination);
      if (!controlledPagination) setInternalPagination(newPagination);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination,
    manualSorting,
    manualFiltering,
    enableRowSelection,
  });

  // Include internal state in deps so context consumers re-render on any table state change.
  // The `table` ref is stable (useReactTable returns the same object), so without these
  // deps the context value never changes and consumers like DataTableContent miss updates.
  const contextValue = useMemo<DataTableContextValue<TData>>(
    () => ({ table, isLoading, isFetching }),
    [table, isLoading, isFetching, sorting, internalFilters, internalVisibility, rowSelection, pagination]
  );

  return (
    <DataTableContext.Provider value={contextValue as DataTableContextValue<unknown>}>
      {children}
    </DataTableContext.Provider>
  );
}
